import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Row, Col, Statistic, Typography, Space } from 'antd';
import {
  ProjectOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  TeamOutlined,
  CalendarOutlined,
  FileTextOutlined,
  LineChartOutlined,
  RightOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { projectKeys } from '@/hooks/useProjects';
import { projectService } from '@/services/project.service';
import { AedDirhamSign } from '@/components/currency';

const { Title, Paragraph, Text } = Typography;

export const ProjectPortfolioDashboardPage: React.FC = () => {
  const navigate = useNavigate();

  const { data: summary, isLoading } = useQuery({
    queryKey: [...projectKeys.all, 'portfolio-summary'],
    queryFn: () => projectService.getPortfolioSummary(),
    staleTime: 60_000,
  });

  const modules = [
    {
      title: 'Projects',
      icon: <ProjectOutlined style={{ fontSize: '32px', color: '#145A54' }} />,
      description: 'Manage projects and track progress',
      stats: {
        label: 'Active projects',
        value: summary?.active_projects ?? 0,
        loading: isLoading,
      },
      path: '/prj/projects',
    },
    {
      title: 'Timesheets',
      icon: <ClockCircleOutlined style={{ fontSize: '32px', color: '#52c41a' }} />,
      description: 'Track time spent on projects',
      stats: {
        label: 'Hours this week',
        value: Number(summary?.hours_this_week ?? 0).toFixed(1),
        loading: isLoading,
      },
      path: '/prj/timesheets',
    },
    {
      title: 'Project costs',
      icon: <DollarOutlined style={{ fontSize: '32px', color: '#fa8c16' }} />,
      description: 'Monitor project expenses and posted costs',
      stats: {
        label: 'Posted cost (total)',
        value: summary?.total_posted_cost ?? 0,
        prefix: <AedDirhamSign />,
        loading: isLoading,
      },
      path: '/prj/costs',
    },
    {
      title: 'Project health',
      icon: <LineChartOutlined style={{ fontSize: '32px', color: '#cf1322' }} />,
      description: 'Open the 360-degree project health check hub',
      stats: { label: 'Open module', value: '—' },
      path: '/prj/project-health',
    },
    {
      title: 'Employees',
      icon: <TeamOutlined style={{ fontSize: '32px', color: '#722ed1' }} />,
      description: 'Manage employee records and details',
      stats: { label: 'Open module', value: '—' },
      path: '/hr/employees',
    },
    {
      title: 'Attendance',
      icon: <CalendarOutlined style={{ fontSize: '32px', color: '#eb2f96' }} />,
      description: 'Track employee attendance',
      stats: { label: 'Open module', value: '—' },
      path: '/hr/attendance',
    },
    {
      title: 'Leave management',
      icon: <CalendarOutlined style={{ fontSize: '32px', color: '#13c2c2' }} />,
      description: 'Manage leave requests and balances',
      stats: { label: 'Open module', value: '—' },
      path: '/hr/leaves',
    },
    {
      title: 'HR documents',
      icon: <FileTextOutlined style={{ fontSize: '32px', color: '#145A54' }} />,
      description: 'Centralized HR document management',
      stats: { label: 'Open module', value: '—' },
      path: '/hr/documents',
    },
  ];

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={2}>Projects &amp; job costing</Title>
          <Paragraph type="secondary">
            Portfolio overview, time, costs, and links to HR
          </Paragraph>
        </div>

        <Card size="small">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Total projects"
                value={summary?.total_projects ?? 0}
                loading={isLoading}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Contract value (excl. cancelled)"
                value={summary?.total_contract_value ?? 0}
                prefix={<AedDirhamSign />}
                loading={isLoading}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Pending timesheet approvals"
                value={summary?.pending_timesheet_approvals ?? 0}
                loading={isLoading}
                prefix={
                  (summary?.pending_timesheet_approvals ?? 0) > 0 ? (
                    <WarningOutlined style={{ color: '#faad14' }} />
                  ) : undefined
                }
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title="Posted cost (total)"
                value={summary?.total_posted_cost ?? 0}
                prefix={<AedDirhamSign />}
                loading={isLoading}
              />
            </Col>
          </Row>
        </Card>

        <Row gutter={[24, 24]}>
          {modules.map((module, index) => (
            <Col xs={24} md={12} lg={8} key={index}>
              <Card hoverable onClick={() => navigate(module.path)} style={{ height: '100%' }}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    {module.icon}
                    <RightOutlined style={{ color: '#8c8c8c' }} />
                  </div>
                  <div>
                    <Title level={4} style={{ marginBottom: 8 }}>
                      {module.title}
                    </Title>
                    <Text type="secondary">{module.description}</Text>
                  </div>
                  <Statistic
                    title={module.stats.label}
                    value={module.stats.value as any}
                    prefix={(module.stats as any).prefix}
                    loading={(module.stats as any).loading}
                    valueStyle={{ fontSize: '24px', fontWeight: 'bold' }}
                  />
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      </Space>
    </div>
  );
};
